/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import {
  Wrapper,
  Header,
  ClickableHeader,
  HeaderLeft,
  HeaderRight,
  Title,
  TitleIcon,
  Content
} from './FeelPlaygroundPanel.styled';

/**
 * @typedef {Object} FeelPlaygroundPanelProps
 * @property {string} [title] - Title of the panel
 * @property {React.ReactNode} [titleIcon] - Icon appearing to the left of the title
 * @property {React.ReactNode} [children] - Content to display in the panel
 * @property {React.ReactNode} [headerItems] - Additional items to display in the header
 * @property {() => void} [onHeaderClick] - Callback when the header is clicked
 * @property {boolean} [isCollapsed] - If true, the panel will only take up the height of the header
 *                                    If false (default), the panel will take up the height defined in later parameters
 * @property {string} [expandedHeight] - If non-null, the panel will take up this height of the parent. Useful for overriding fullHeight
 *                                If null (default), the panel will fill up the remaining height.
 */

/**
 * A panel component for the FEEL playground
 * @param {FeelPlaygroundPanelProps} props - Component props
 * @returns {JSX.Element} The rendered panel component
 */
export const FeelPlaygroundPanel = ({
  title,
  titleIcon,
  children,
  headerItems,
  onHeaderClick,
  isCollapsed,
  expandedHeight
}) => {
  const HeaderComponent = onHeaderClick ? ClickableHeader : Header;

  return (
    <Wrapper $isCollapsed={isCollapsed} $expandedHeight={expandedHeight} data-testid="feel-panel" aria-label={title}>
      <HeaderComponent onClick={onHeaderClick}>
        <HeaderLeft>
          {titleIcon && <TitleIcon>{titleIcon}</TitleIcon>}
          <Title>{title}</Title>
        </HeaderLeft>
        {headerItems && <HeaderRight>{headerItems}</HeaderRight>}
      </HeaderComponent>
      <Content>{children}</Content>
    </Wrapper>
  );
};
